import React from "react";
import i18n from "../i18n/i18n";
import {ReactComponent as InIcon} from "../statics/icons/icon_in.svg";
import {ReactComponent as OutIcon} from "../statics/icons/icon_out.svg";
import {PhotosModel} from "../transportation/models/photos";
import {DamageModelWithDirection} from "../transportation/models/tcr";
import {Direction, SideType} from "../transportation/models/transport";
import {ReactComponent as DangerIcon} from "../statics/icons/icon_danger.svg";
import {ReactComponent as TemperatureIcon} from "../statics/icons/icon_temperature.svg";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import Validator from "./validator";
import {ValidatedValue} from "./validatedValue";
import {PortModel} from "../dictionaries/models/ports";
import {IOrderStore} from "../transportation/stores/orderStore";
import {ITicketStore} from "../transportation/stores/ticketStore";
import {FerryDropDownModel} from "../dictionaries/models/ferry";
import {ShipownerService} from "../dictionaries/services/shipownerService";
import {CountryService} from "../common/services/countryService";
import {RelationType} from "../transportation/models/relation";
import {IDangerousGoodInStore} from "../transportation/stores/dangerousGoodInStore";
import {DangerousGoodsInNewTicket} from "../transportation/models/ticket";
import {DangerousGoodsInNewOrUpdateOrder} from "../transportation/models/dangeresGoods";
import {Utils} from "./utils";

interface SideWithoutData {
    sideType: SideType;
}

export default class TransportUtils {
    public static renderSideType(prefix: string, value: string): JSX.Element {
        return (
            <div className="side-type">
                <span className="side-type-first-letter">
                    {i18n.t(`${prefix}.sideTypeFirstLetter.${value}`)}
                </span>
                <span>
                    {i18n.t(`${prefix}.sideType.${value}`)}
                </span>
            </div>
        );
    }

    public static renderDirection(value: string): JSX.Element {
        switch (value) {
            case Direction.IN:
                return <InIcon/>;
            case Direction.OUT:
                return <OutIcon/>;
            default:
                return <></>;
        }
    }

    public static renderDamageOrPhotoRows(type: "tcr" | "photos", data: DamageModelWithDirection[] | PhotosModel[]): DamageModelWithDirection[] | PhotosModel[] | SideWithoutData[] {
        const sides = new Map<SideType, DamageModelWithDirection[] | PhotosModel[] | SideWithoutData[]>();
        const isPhotos = type === "photos";

        if (data) {
            this.addDamageOrPhotoRow(sides, SideType.FRONT, data);
            this.addDamageOrPhotoRow(sides, SideType.REAR, data);
            this.addDamageOrPhotoRow(sides, SideType.RIGHT, data);
            this.addDamageOrPhotoRow(sides, SideType.LEFT, data);

            if (isPhotos) {
                this.addDamageOrPhotoRow(sides, SideType.TOP, data);
            }

            sides.forEach((value, key) => {
                if (!value.length) {
                    sides.set(key, [{
                        sideType: key
                    }]);
                }
            });

            const result = [
                ...sides.get(SideType.FRONT)!,
                ...sides.get(SideType.REAR)!,
                ...sides.get(SideType.RIGHT)!,
                ...sides.get(SideType.LEFT)!
            ];

            if (isPhotos) {
                return [
                    ...result,
                    ...sides.get(SideType.TOP)!
                ];
            } else {
                return result;
            }
        } else return [];
    }

    private static addDamageOrPhotoRow(sides: Map<SideType, DamageModelWithDirection[] | PhotosModel[] | SideWithoutData[]>, sideType: SideType, data: DamageModelWithDirection[] | PhotosModel[]): void {
        const side = (data as (PhotosModel | DamageModelWithDirection)[]).filter(x => x.sideType === sideType);
        const inDirection = side.filter(x => x.direction === Direction.IN);
        const outDirection = side.filter(x => x.direction === Direction.OUT);

        sides.set(sideType, [...inDirection, ...outDirection]);
    }

    public static renderInfoIcons(row: any): JSX.Element {
        if (!row) {
            return <></>;
        }

        const isDangerous = row.isDangerous;
        const isTemperatureRequired = row.isTemperatureRequired;
        const isOversize = row.isOversize;
        const renderedInfo: JSX.Element[] = [];

        if (isDangerous) {
            renderedInfo.push(<DangerIcon key="dangerous-icon" data-testid="dangerous-icon"/>);
        }
        if (isTemperatureRequired) {
            renderedInfo.push(<TemperatureIcon key="temperature-icon" data-testid="temperature-icon"/>);
        }
        if (isOversize) {
            renderedInfo.push(<ZoomOutMapIcon key="oversize-icon" data-testid="oversize-icon"/>);
        }

        if (!isDangerous && !isTemperatureRequired && !isOversize) {
            renderedInfo.push(<span key="empty" data-testid="empty">-</span>);
        }

        return <div className="info-icons">{renderedInfo}</div>;
    }

    public static dateToIsGreaterThanTheMonthBefore(dateFrom: ValidatedValue<Date | null>, dateTo: ValidatedValue<Date | null>): boolean {
        dateFrom.validate();
        dateTo.validate();

        if (dateFrom.isValid && dateTo.isValid) {
            if (dateFrom.value && Validator.isCorrectDateWithTime(dateFrom.value) && dateTo.value && Validator.isCorrectDateWithTime(dateTo.value)) {
                return Validator.differenceBetweenDatesIsGreaterThanMonth(dateFrom.value, dateTo.value);
            } else {
                const currentDataWithTime = new Date();
                currentDataWithTime.setSeconds(0);
                currentDataWithTime.setMilliseconds(0);

                if (dateFrom.value && Validator.isCorrectDateWithTime(dateFrom.value) && !dateTo.value) {
                    return Validator.differenceBetweenDatesIsGreaterThanMonth(currentDataWithTime, dateFrom.value);
                } else if (dateTo.value && Validator.isCorrectDateWithTime(dateTo.value) && !dateFrom.value) {
                    return Validator.differenceBetweenDatesIsGreaterThanMonth(currentDataWithTime, dateTo.value);
                } else return false;
            }
        }
        return false;
    }

    public static async setOriginDestinationCountry(ports: PortModel[], store: IOrderStore | ITicketStore, value: string): Promise<void> {
        if (!ports || ports.length <= 0) {
            return;
        }

        const port = ports.find((port) => port.id === value);

        if (port) {
            store.setOriginDestinationCountry(port.country.imoCode);
            store.originDestinationCountry.clearValidation();
            const response = await CountryService.getAll() || [];
            const country = response?.data?.data?.find(country => country.imoCode === port.country.imoCode);
            store.setOriginDestinationCountryName(country?.name || "");
            store.originDestinationCountryName.clearValidation();
        }
    }

    public static async setShipowner(ferries: FerryDropDownModel[], store: IOrderStore | ITicketStore, value: string): Promise<void> {
        if (!ferries || ferries.length <= 0) {
            return;
        }

        const ferry = ferries.find((ferry) => ferry.id === value);

        if (ferry) {
            store.setShipownerId(ferry.shipownerId);
            store.shipownerId.clearValidation();
            const response = await ShipownerService.getShipowner(ferry.shipownerId);
            store.setShipownerName(response?.data?.name || "");
            store.shipownerName.clearValidation();
        }
    }

    public static isRelationTypeWithoutDepo(relationType: "" | RelationType | undefined) {
        switch (relationType) {
            case RelationType.RAIL_FERRY:
            case RelationType.FERRY_RAIL:
                return true;
            default:
                return false;
        }
    }

    public static prepareDangerousGoods(dangerousGoods: IDangerousGoodInStore[]): DangerousGoodsInNewOrUpdateOrder[] | DangerousGoodsInNewTicket[]| null {
        const preparedDangerousGoods: DangerousGoodsInNewOrUpdateOrder[] = [];

        for (const dangerousGoodsElement of dangerousGoods) {
            if (this.isDangerousGoodNotEmpty(dangerousGoodsElement)) {
                preparedDangerousGoods.push({
                    packing: dangerousGoodsElement.packingId.value,
                    packingGroup: dangerousGoodsElement.packingGroup.value,
                    packingQuantity: dangerousGoodsElement.packingQuantity.value!,
                    UNCode: dangerousGoodsElement.unCodeId.value,
                    weight: dangerousGoodsElement.weight.value ? Number(Utils.changeCommaToDot(dangerousGoodsElement.weight.value!)) : null
                });
            }
        }

        return preparedDangerousGoods.length !== 0 ? preparedDangerousGoods : null;
    }

    private static isDangerousGoodNotEmpty(dangerousGood: IDangerousGoodInStore): boolean {
        return !!(dangerousGood.packingId.value ||
            dangerousGood.packingGroup.value ||
            dangerousGood.packingQuantity.value ||
            dangerousGood.unCodeId.value ||
            dangerousGood.weight.value
        );
    }

    public static isNoSeaTrafficRelationType(relationType: RelationType | undefined | ""): boolean {
        if (!relationType) return false;

        return (
            relationType === RelationType.ROAD_DEPO_ROAD ||
            relationType === RelationType.RAIL_DEPO_RAIL ||
            relationType === RelationType.ROAD_DEPO_RAIL ||
            relationType === RelationType.RAIL_DEPO_ROAD ||
            relationType === RelationType.RAIL_DEPO ||
            relationType === RelationType.ROAD_DEPO
        );
    }

    // Generate time in hh:mm format
    public static generateTimeInHoursWithMinutesFormat(timeInMinutes: number, isDash: boolean) {
        const hours = Math.floor(timeInMinutes/60);
        const minutes = timeInMinutes - (60 * hours);

        const returnHours = (hours >= 0 && hours < 10) ? `0${hours}` : hours;
        const returnMinutes = (minutes >= 0 && minutes < 10) ? `0${minutes}` : minutes;

        if (hours === 0 && minutes === 0) {
            return isDash ? "-" : "";
        } else {
            return `${returnHours}:${returnMinutes}`;
        }
    }
}